import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/api/pos.api';
import type { PosShift, CloseShiftRequest } from '@/api/pos.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePosSession } from '@/hooks/usePosSession';
import { formatCurrency } from '@/utils/format';
import { PageHeader } from '@/components/layout/PageHeader';
import { notify } from '@/core/toast/notify';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
/* ─── Styles ─────────────────────────────────────────────────────────────── */
const card = 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900';
const metricCard = 'rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60';
/* ─── StatusBadge ────────────────────────────────────────────────────────── */
function StatusBadge({ status }: { status: PosShift['shiftStatus'] }) {
  const cfg = {
    OPEN:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    ACTIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    CLOSED: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  }[status];
  const dot = { OPEN: '🟡', ACTIVE: '🟢', CLOSED: '⚪' }[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg}`}>
      {dot} {status}
    </span>
  );
}

/* ─── CloseShiftPanel ────────────────────────────────────────────────────── */
function CloseShiftPanel({
  shift,
  currencyCode,
  onClose,
  onClosed,
}: {
  shift: PosShift;
  currencyCode: string;
  onClose: () => void;
  onClosed: () => void;
}) {
  const [closingCash, setClosingCash] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (req: CloseShiftRequest) => posApi.closeShift(shift.id, req),
    onSuccess: () => {
      onClosed();
      notify.success('Shift closed successfully');
      qc.invalidateQueries({ queryKey: ['pos-shift'] });
      onClose();
    },
    onError: () => notify.error('Failed to close shift'),
  });

  const cash = parseFloat(closingCash) || 0;
  const expected = (shift.openingCash ?? 0) + (shift.cashSales ?? 0);
  const discrepancy = cash - expected;

  return (
    <div className="space-y-4">
      <Input
        type="number"
        min={0}
        step="0.01"
        label="Closing cash in drawer"
        placeholder="Enter amount counted"
        value={closingCash}
        onChange={e => setClosingCash(e.target.value)}
        autoFocus
      />

      {closingCash && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Opening cash', value: shift.openingCash },
            { label: 'Cash sales', value: shift.cashSales },
            { label: 'Expected', value: expected },
          ].map(m => (
            <div key={m.label} className={metricCard}>
              <p className="text-xs text-slate-500">{m.label}</p>
              <p className="mt-1 text-base font-bold text-slate-900 dark:text-white">
                {formatCurrency(m.value, currencyCode)}
              </p>
            </div>
          ))}
        </div>
      )}

      {closingCash && (
        <div className={`rounded-xl p-4 ${
          discrepancy < 0
            ? 'bg-red-50 border border-red-200 dark:bg-red-950/30 dark:border-red-800'
            : discrepancy > 0
              ? 'bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
              : 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800'
        }`}>
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">Cash discrepancy</p>
          <p className={`text-2xl font-bold ${
            discrepancy < 0 ? 'text-red-600' : discrepancy > 0 ? 'text-amber-600' : 'text-emerald-600'
          }`}>
            {discrepancy >= 0 ? '+' : ''}{formatCurrency(discrepancy, currencyCode)}
          </p>
          {discrepancy !== 0 && (
            <p className="mt-0.5 text-xs text-slate-500">
              {discrepancy < 0 ? '⚠ Short — cash missing from drawer' : '⚠ Over — more cash than expected'}
            </p>
          )}
        </div>
      )}

      {discrepancy !== 0 && closingCash && (
        <Input
          type="text"
          label="Discrepancy reason"
          placeholder="Explain the discrepancy..."
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      )}

      <Input type="text" label="Notes (optional)" placeholder="Any shift notes..." value={notes} onChange={e => setNotes(e.target.value)} />

      <div className="flex gap-3">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={!closingCash || isPending}
          loading={isPending}
          onClick={() => mutate({ closingCash: cash, discrepancyReason: reason || undefined, notes: notes || undefined })}
        >
          Close Shift
        </Button>
      </div>
    </div>
  );
}

/* ─── OpenShiftPanel ─────────────────────────────────────────────────────── */
function OpenShiftPanel({
  storeId,
  counterId,
  currencyCode,
  onOpened,
}: {
  storeId: number;
  counterId: number;
  currencyCode: string;
  onOpened: (shiftId: number) => void;
}) {
  const [openingCash, setOpeningCash] = useState('');
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      posApi.openShift({ storeId, counterId, openingCash: parseFloat(openingCash) || 0, notes: notes || undefined }),
    onSuccess: (shift) => {
      onOpened(shift.id);
      notify.success('Shift opened');
      qc.invalidateQueries({ queryKey: ['pos-shift'] });
    },
    onError: () => notify.error('Failed to open shift'),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-primary-50 p-4 text-sm text-primary-700 dark:bg-primary-950/30 dark:text-primary-300">
        Opening a shift registers your starting cash and ties all transactions to this session for end-of-day reconciliation.
      </div>
      <Input
        type="number"
        min={0}
        step="0.01"
        label="Opening cash in drawer"
        placeholder={`0.00 ${currencyCode}`}
        value={openingCash}
        onChange={e => setOpeningCash(e.target.value)}
        autoFocus
      />
      <Input type="text" label="Notes (optional)" placeholder="E.g. float from safe" value={notes} onChange={e => setNotes(e.target.value)} />
      <Button className="w-full" size="lg" disabled={isPending} loading={isPending} onClick={() => mutate()}>
        🟢 Open Shift
      </Button>
    </div>
  );
}

/* ─── ActiveShiftMetrics ─────────────────────────────────────────────────── */
function ActiveShiftMetrics({ shift, currencyCode }: { shift: PosShift; currencyCode: string }) {
  const fmt = (v: number) => formatCurrency(v, currencyCode);
  const metrics = [
    { label: 'Total Sales', value: fmt(shift.totalSales), icon: '💰' },
    { label: 'Transactions', value: String(shift.transactionCount), icon: '🧾' },
    { label: 'Returns', value: String(shift.returnCount), icon: '↩️' },
    { label: 'Cash Sales', value: fmt(shift.cashSales), icon: '💵' },
    { label: 'Card Sales', value: fmt(shift.cardSales), icon: '💳' },
    { label: 'UPI Sales', value: fmt(shift.upiSales), icon: '📱' },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {metrics.map(m => (
        <div key={m.label} className={metricCard}>
          <p className="text-lg">{m.icon}</p>
          <p className="mt-1 text-xs text-slate-500">{m.label}</p>
          <p className="mt-0.5 text-base font-bold text-slate-900 dark:text-white">{m.value}</p>
        </div>
      ))}
    </div>
  );
}

/* ─── Main Dashboard ─────────────────────────────────────────────────────── */
export function PosShiftDashboard({ counterId }: { counterId: number }) {
  const store = useAppSelector(s => s.app.selectedStore);
  const { setActiveShiftId, refreshActiveShift } = usePosSession();
  const currencyCode = store?.currencyCode ?? 'INR';
  const [closingMode, setClosingMode] = useState(false);

  const { data: activeShift, isLoading } = useQuery({
    queryKey: ['pos-shift', 'active', counterId],
    queryFn: () => posApi.getActiveShift(counterId).catch(() => null),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (activeShift && activeShift.shiftStatus !== 'CLOSED') {
      setActiveShiftId(activeShift.id);
    }
  }, [activeShift, setActiveShiftId]);

  if (isLoading) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4 pb-8 sm:p-6">
      <PageHeader
        title="Shift Management"
        description={`Counter #${counterId}`}
        actions={activeShift ? <StatusBadge status={activeShift.shiftStatus} /> : undefined}
      />

      {/* No active shift → Open shift */}
      {!activeShift && !isLoading && (
        <div className={card}>
          <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-white">Open a New Shift</h2>
          {store && (
            <OpenShiftPanel
              storeId={store.id}
              counterId={counterId}
              currencyCode={currencyCode}
              onOpened={(id) => {
                setActiveShiftId(id);
                void refreshActiveShift();
              }}
            />
          )}
        </div>
      )}

      {/* Active shift */}
      {activeShift && !closingMode && (
        <>
          <div className={card}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Shift opened</p>
                <p className="text-sm font-semibold text-slate-800 dark:text-white">
                  {new Date(activeShift.openedAt).toLocaleString()}
                </p>
                {activeShift.employeeName && (
                  <p className="text-xs text-slate-500">by {activeShift.employeeName}</p>
                )}
              </div>
              <Button variant="danger" onClick={() => setClosingMode(true)}>
                Close Shift
              </Button>
            </div>
            <ActiveShiftMetrics shift={activeShift} currencyCode={currencyCode} />
          </div>
        </>
      )}

      {/* Close shift form */}
      {activeShift && closingMode && (
        <div className={card}>
          <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-white">Close Shift</h2>
          <CloseShiftPanel
            shift={activeShift}
            currencyCode={currencyCode}
            onClose={() => setClosingMode(false)}
            onClosed={() => {
              setActiveShiftId(undefined);
              void refreshActiveShift();
            }}
          />
        </div>
      )}
    </div>
  );
}
